import { Group, Loader } from '@mantine/core';
import type { CatalogRule, Filter } from '@repo/api-codegen';
import {
	getExcludedFiltersQueryKey,
	getIncludedFiltersQueryKey,
	useGetExcludedFilters,
	useGetIncludedFilters,
	useSetExcludedFilters,
	useSetIncludedFilters,
} from '@repo/api-codegen';
import { SettingsBlock } from '@repo/common/components';
import { useCallback, useMemo } from 'react';
import { queryClient, useDataQualityAccess } from '../../../api';
import {
	DEFAULT_FILTER_OPTIONS,
	DEFAULT_FILTER_OPTIONS_WITH_DQS,
	FILTER_OPTIONS_DIVIDER,
} from '../../Filter';
import { FILTER_OPTIONS_CONFIG } from '../../Filter/constants';
import { GovernanceFilters } from '../GovernanceFilters/GovernanceFilters';

interface IAIPersonasFiltersProps {
	personaId: string;
}

export function AIPersonasFilters({ personaId }: IAIPersonasFiltersProps) {
	const { data: includedFilters, isLoading: isLoadingIncluded } =
		useGetIncludedFilters({
			pathParams: {
				personaId,
			},
		});
	const { mutateAsync: updateIncludedFilters } = useSetIncludedFilters({
		onSuccess: (_, variables) => {
			// optimistic update
			queryClient.setQueryData(
				getIncludedFiltersQueryKey({
					pathParams: {
						personaId,
					},
				}),
				variables.body
			);
		},
	});
	const { data: excludedFilters, isLoading: isLoadingExcluded } =
		useGetExcludedFilters({
			pathParams: {
				personaId,
			},
		});
	const { mutateAsync: updateExcludedFilters } = useSetExcludedFilters({
		onSuccess: (_, variables) => {
			// optimistic update
			queryClient.setQueryData(
				getExcludedFiltersQueryKey({
					pathParams: {
						personaId,
					},
				}),
				variables.body
			);
		},
	});

	const handleOnIncludedFiltersChange = useCallback(
		async (rule: CatalogRule) => {
			await updateIncludedFilters({
				body: rule.operands,
				pathParams: { personaId },
			});
		},
		[updateIncludedFilters, personaId]
	);

	const handleOnExcludedFiltersChange = useCallback(
		async (rule: CatalogRule) => {
			await updateExcludedFilters({
				body: rule.operands,
				pathParams: { personaId },
			});
		},
		[updateExcludedFilters, personaId]
	);

	// This must match the backend implementation
	const filter: Filter = useMemo(() => {
		const operands: Filter[] = [];

		if (includedFilters && includedFilters.length > 0) {
			operands.push({
				operator: 'or',
				operands: includedFilters,
			});
		}

		if (excludedFilters && excludedFilters.length > 0) {
			operands.push({
				operator: 'not',
				operands: excludedFilters,
			});
		}

		return {
			operator: 'and',
			operands: operands,
		};
	}, [includedFilters, excludedFilters]);

	const dqsEnabled = useDataQualityAccess();
	const filterOptions = dqsEnabled
		? DEFAULT_FILTER_OPTIONS_WITH_DQS
		: DEFAULT_FILTER_OPTIONS;

	// Top level operator is always or in the backend
	// It's added here just for typing, but when passing to the backend, we strip out the operator and pass only the operands
	const includedCatalogFilters: CatalogRule = useMemo(
		() => ({ operator: 'or', operands: includedFilters ?? [] }),
		[includedFilters]
	);
	const excludedCatalogFilters: CatalogRule = useMemo(
		() => ({ operator: 'or', operands: excludedFilters ?? [] }),
		[excludedFilters]
	);

	if (isLoadingIncluded || isLoadingExcluded) {
		return (
			<Group position="center">
				<Loader />
			</Group>
		);
	}

	return (
		<SettingsBlock
			title="Resource access"
			description="Create rules to exclude resources from the Persona. By default the Persona can access all resources in the workspace"
		>
			<GovernanceFilters
				previewFilter={filter}
				emptyStateMessage="Currently, the AI assistant can access all resources in your workspace (subject to permissions of the user asking the question). To limit the resources, add Resource Access filters."
				includedFilters={includedCatalogFilters}
				excludedFilters={excludedCatalogFilters}
				onIncludedFiltersChange={handleOnIncludedFiltersChange}
				onExcludedFiltersChange={handleOnExcludedFiltersChange}
				filterOptions={filterOptions.map((option) =>
					option === FILTER_OPTIONS_DIVIDER
						? option
						: FILTER_OPTIONS_CONFIG[option]
				)}
				disableTopLevelOperatorToggle
			/>
		</SettingsBlock>
	);
}
